import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { FloatingLabelInput } from '@/shared/components/custom/form-field';
import { SelectItem } from '@/shared/components/ui/select';
import { FloatingLabelSelect } from '@/shared/components/custom/form-field';
import type {
  ProjectFolderNodeDto,
  CreateProjectFolderInput,
  RenameProjectFolderInput,
} from '../types';

interface FolderFormDialogProps {
  open: boolean;
  mode: 'create' | 'rename';
  folders: ProjectFolderNodeDto[];
  initialName?: string;
  initialParentId?: string | null;
  onSubmitCreate: (data: CreateProjectFolderInput) => void;
  onSubmitRename: (data: RenameProjectFolderInput) => void;
  onCancel: () => void;
}

function flattenFolders(nodes: ProjectFolderNodeDto[], depth = 0): { id: string; label: string }[] {
  return nodes.flatMap((node) => {
    if (!node.id || !node.name) return [];
    const prefix = '  '.repeat(depth);
    return [
      { id: node.id, label: `${prefix}${node.name}` },
      ...flattenFolders(node.children ?? [], depth + 1),
    ];
  });
}

export function FolderFormDialog({
  open,
  mode,
  folders,
  initialName = '',
  initialParentId = null,
  onSubmitCreate,
  onSubmitRename,
  onCancel,
}: FolderFormDialogProps) {
  const { t } = useTranslation('projectFiles');
  const { t: tCommon } = useTranslation('common');
  const [name, setName] = useState(initialName);
  const ROOT = '__root__';
  const [parentId, setParentId] = useState<string>(initialParentId ?? ROOT);
  const [error, setError] = useState('');

  const flatFolders = flattenFolders(folders);

  function handleSubmit() {
    if (!name.trim()) {
      setError(t('form.validation.nameRequired'));
      return;
    }
    if (mode === 'create') {
      onSubmitCreate({ name: name.trim(), parentFolderId: parentId === ROOT ? null : parentId });
    } else {
      onSubmitRename({ name: name.trim() });
    }
  }

  const title = mode === 'create' ? t('folder.createDialogTitle') : t('folder.renameDialogTitle');

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) onCancel();
      }}
    >
      <DialogContent data-testid="project-files-folder-form-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <FloatingLabelInput
            id="folder-name"
            label={t('folder.nameLabel')}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setError('');
            }}
            invalid={!!error}
            data-testid="project-files-folder-form-name"
          />
          {error && <p className="text-xs text-destructive">{error}</p>}

          {mode === 'create' && (
            <FloatingLabelSelect
              id="folder-parent"
              label={t('folder.parentLabel')}
              value={parentId}
              onValueChange={setParentId}
              hasValue={!!parentId}
            >
              <SelectItem value={ROOT}>{t('item.moveDialog.root')}</SelectItem>
              {flatFolders.map((f) => (
                <SelectItem key={f.id} value={f.id}>
                  {f.label}
                </SelectItem>
              ))}
            </FloatingLabelSelect>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            {tCommon('actions.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleSubmit}>
            {mode === 'create' ? tCommon('actions.create', 'Create') : t('folder.rename')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
